import axios from 'axios';
import progressBus from './progressBus';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || (
        process.env.NODE_ENV === 'production'
            ? 'https://origin-api.bnvduotutor.com/api'
            : 'http://localhost:5000/api'
    ),
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// 요청 인터셉터에서 토큰을 헤더에 추가
api.interceptors.request.use(
    (config) => {
        progressBus.start();
        const auth = localStorage.getItem('auth');
        if (auth) {
            const { token } = JSON.parse(auth);
            config.headers.Authorization = `Bearer ${token}`;
        } else {
            // 'auth'가 없으면 게스트 토큰('guestAuth')으로 폴백
            try {
                const guest = JSON.parse(localStorage.getItem('guestAuth') || 'null');
                if (guest && guest.token) {
                    config.headers.Authorization = `Bearer ${guest.token}`;
                }
            } catch {
                // guestAuth 파싱 실패 시 무시
            }
        }
        return config;
    },
    (error) => {
        console.error('Request Error:', error);
        return Promise.reject(error);
    }
);

// 응답 인터셉터 (401, 403 등 인증/권한 관련 에러 처리 통합)
api.interceptors.response.use(
    response => {
        progressBus.done();
        return response;
    },
    async error => {
        progressBus.done();
        const originalRequest = error.config;

        // 네트워크 에러 처리 (서버 응답 없음)
        if (!error.response) {
            console.error('Network Error:', error.message);
            return Promise.reject(error);
        }

        const { status } = error.response;

        // 로그인/회원가입 요청은 리다이렉트하지 않음
        const isAuthRequest = originalRequest.url?.includes('/auth/login') ||
                              originalRequest.url?.includes('/auth/signup') ||
                              originalRequest.url?.includes('/auth/refresh-token');

        // 401 Unauthorized - 토큰 갱신 시도
        if (status === 401 && !isAuthRequest) {
            // 게스트(수업코드 입장) 세션은 계정 리프레시/‑login 로직 대상이 아니다.
            // 계정 토큰('auth')이 없으면 게스트이며, 게스트 401 은 토큰 만료/코드 재발급(codeVersion 변경)을 뜻한다.
            // 기존엔 계정 refresh 시도가 실패하며 /login 으로 반복 이동해 '하얀 화면'(루프)이 됐다.
            // → 게스트 세션을 정리하고 입장 화면(/login 학생 모드)으로 1회만 이동한 뒤 에러를 전파한다.
            if (!localStorage.getItem('auth')) {
                localStorage.removeItem('guestAuth');
                if (window.location.pathname !== '/login') {
                    window.location.href = '/login?mode=student';
                }
                return Promise.reject(error);
            }

            // 이미 재시도한 요청은 다시 시도하지 않음
            if (originalRequest._retry) {
                localStorage.removeItem('auth');
                window.location.href = '/login';
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            try {
                const refreshResponse = await api.post('/auth/refresh-token');
                const auth = JSON.parse(localStorage.getItem('auth') || '{}');
                const newAuth = {
                    ...auth,
                    token: refreshResponse.data.token
                };
                localStorage.setItem('auth', JSON.stringify(newAuth));

                originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.token}`;
                return api(originalRequest);
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                localStorage.removeItem('auth');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }

        // 403 Forbidden - 권한 없음
        if (status === 403) {
            console.warn('Permission denied:', error.response.data);
            // 권한 에러는 그대로 전파 (컴포넌트에서 처리)
        }

        // 500 Internal Server Error
        if (status >= 500) {
            console.error('Server Error:', error.response.data);
            // 서버 에러도 그대로 전파
        }

        return Promise.reject(error);
    }
);

const corsOptions = {
    origin: [
        'https://www.bnvduotutor.com',
        'https://bnvduotutor.com',
        'https://api.bnvduotutor.com'
    ],
    credentials: true,
    methods: ['GET', 'POST', 'PUT', 'DELETE', 'OPTIONS'],
    allowedHeaders: ['Content-Type', 'Authorization']
};

export default api;
